import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ARTIKEL_ETIKETT_MAX,
  toArtikelEtikettItem,
  type ArtikelEtikettItem,
} from "@/lib/artikelEtikett";

export const ARTIKEL_ETIKETT_CART_KEY = "ernsthaus.artikel.etikett.cart";
export const ARTIKEL_ETIKETT_CART_EVENT = "ernsthaus-etikett-cart";

function isCartItem(value: unknown): value is ArtikelEtikettItem {
  if (!value || typeof value !== "object") return false;
  const item = value as ArtikelEtikettItem;
  return Number.isFinite(item.id) && item.id > 0 && typeof item.heroNr === "string" && typeof item.name === "string";
}

export function parseArtikelEtikettCart(raw: unknown): ArtikelEtikettItem[] {
  if (!Array.isArray(raw)) return [];
  const out: ArtikelEtikettItem[] = [];
  const seen = new Set<number>();
  for (const value of raw) {
    if (!isCartItem(value) || seen.has(value.id)) continue;
    seen.add(value.id);
    out.push({
      id: value.id,
      heroNr: value.heroNr.trim() || `#${value.id}`,
      name: value.name.trim(),
    });
    if (out.length >= ARTIKEL_ETIKETT_MAX) break;
  }
  return out;
}

export function loadArtikelEtikettCart(): ArtikelEtikettItem[] {
  if (typeof window === "undefined") return [];
  try {
    return parseArtikelEtikettCart(JSON.parse(window.localStorage.getItem(ARTIKEL_ETIKETT_CART_KEY) || "[]"));
  } catch {
    return [];
  }
}

export function saveArtikelEtikettCart(items: ArtikelEtikettItem[]): ArtikelEtikettItem[] {
  const next = parseArtikelEtikettCart(items);
  if (typeof window === "undefined") return next;
  window.localStorage.setItem(ARTIKEL_ETIKETT_CART_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event(ARTIKEL_ETIKETT_CART_EVENT));
  return next;
}

export function artikelFromRowForEtikettCart(row: {
  id: number;
  hero_nr?: string | null;
  hero_base_name?: string | null;
}): ArtikelEtikettItem {
  return toArtikelEtikettItem(row);
}

export function toggleArtikelEtikettCartItem(
  items: ArtikelEtikettItem[],
  item: ArtikelEtikettItem,
): { items: ArtikelEtikettItem[]; action: "added" | "removed" | "full" } {
  const current = parseArtikelEtikettCart(items);
  if (current.some((entry) => entry.id === item.id)) {
    return {
      items: current.filter((entry) => entry.id !== item.id),
      action: "removed",
    };
  }
  if (current.length >= ARTIKEL_ETIKETT_MAX) {
    return { items: current, action: "full" };
  }
  return { items: [...current, parseArtikelEtikettCart([item])[0]!], action: "added" };
}

export function mergeArtikelEtikettCart(
  items: ArtikelEtikettItem[],
  incoming: ArtikelEtikettItem[],
): { items: ArtikelEtikettItem[]; added: number; skipped: number } {
  const next = parseArtikelEtikettCart(items);
  const seen = new Set(next.map((item) => item.id));
  let added = 0;
  let skipped = 0;
  for (const item of parseArtikelEtikettCart(incoming)) {
    if (seen.has(item.id)) continue;
    if (next.length >= ARTIKEL_ETIKETT_MAX) {
      skipped += 1;
      continue;
    }
    seen.add(item.id);
    next.push(item);
    added += 1;
  }
  return { items: next, added, skipped };
}

export function useArtikelEtikettCart() {
  const [items, setItems] = useState<ArtikelEtikettItem[]>(() => loadArtikelEtikettCart());

  useEffect(() => {
    const sync = () => setItems(loadArtikelEtikettCart());
    window.addEventListener(ARTIKEL_ETIKETT_CART_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(ARTIKEL_ETIKETT_CART_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const ids = useMemo(() => new Set(items.map((item) => item.id)), [items]);

  const persist = useCallback((next: ArtikelEtikettItem[]) => {
    setItems(saveArtikelEtikettCart(next));
  }, []);

  const toggle = useCallback(
    (item: ArtikelEtikettItem) => {
      const result = toggleArtikelEtikettCartItem(items, item);
      if (result.action !== "full") persist(result.items);
      return result.action;
    },
    [items, persist],
  );

  const addMany = useCallback(
    (incoming: ArtikelEtikettItem[]) => {
      const result = mergeArtikelEtikettCart(items, incoming);
      persist(result.items);
      return result;
    },
    [items, persist],
  );

  const remove = useCallback(
    (id: number) => persist(items.filter((item) => item.id !== id)),
    [items, persist],
  );

  const clear = useCallback(() => persist([]), [persist]);

  return {
    items,
    ids,
    count: items.length,
    toggle,
    addMany,
    remove,
    clear,
  };
}
